import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import PosterHeader from '../../components/header/PosterHeaderBar/PosterHeader';
import Footer from '../../components/footer/footer';
import './BlogPostPage.css';

function BlogPostPage() {
    const { postId } = useParams();
    const navigate = useNavigate();
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);

    useEffect(() => {
        // Check authentication
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            setUser(currentUser);

            if (currentUser) {
                // Check if the user is an admin
                const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
                if (userDoc.exists() && userDoc.data().isAdmin) {
                    setIsAdmin(true);
                } else {
                    setIsAdmin(false);
                }
            } else {
                setIsAdmin(false);
            }
        });

        // Fetch the blog post
        const fetchPost = async () => {
            try {
                const postDoc = await getDoc(doc(db, 'blogPosts', postId));

                if (postDoc.exists()) {
                    const postData = postDoc.data();

                    // Convert Firestore timestamp to JS Date
                    if (postData.date && postData.date.toDate) {
                        postData.date = postData.date.toDate();
                    }

                    setPost({
                        id: postDoc.id,
                        ...postData
                    });
                } else {
                    console.error('Post not found');
                }

                setLoading(false);
            } catch (error) {
                console.error('Error fetching post:', error);
                setLoading(false);
            }
        };

        fetchPost();

        return () => unsubscribe();
    }, [postId]);

    // Format date for display
    const formatDate = (date) => {
        if (!date) return '';
        return date.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    // Navigate to edit the post
    const handleEdit = () => {
        navigate(`/blog/edit/${postId}`);
    };

    // Go back to all posts
    const handleBack = () => {
        navigate('/blog');
    };

    // Render the blog post content with formatting
    const renderContent = (content) => {
        if (!content) return null;

        // Split by double newlines for paragraphs
        const paragraphs = content.split('\n\n');

        return paragraphs.map((paragraph, index) => {
            // Handle bold text (wrapped in **text**)
            const formattedText = paragraph.replace(
                /\*\*(.*?)\*\*/g,
                '<strong>$1</strong>'
            );

            return (
                <p
                    key={index}
                    dangerouslySetInnerHTML={{ __html: formattedText }}
                    className="post-paragraph"
                />
            );
        });
    };

    return (
        <div className="blog-post-page">
            <PosterHeader />

            <div className="blog-post-container">
                <button onClick={handleBack} className="back-button">
                    ← Back to all posts
                </button>

                {loading ? (
                    <div className="loading">Loading post...</div>
                ) : post ? (
                    <article className="blog-post-content">
                        <div className="post-header">
                            <div className="post-date">{formatDate(post.date)}</div>
                            {post.title && <h1 className="post-title">{post.title}</h1>}

                            {isAdmin && (
                                <button onClick={handleEdit} className="edit-button">
                                    Edit Post
                                </button>
                            )}
                        </div>

                        {post.imageUrl && (
                            <div className="post-image">
                                <img src={post.imageUrl} alt={post.title || 'Blog post'} />
                            </div>
                        )}

                        <div className="post-body">
                            {renderContent(post.content)}
                        </div>
                    </article>
                ) : (
                    <div className="not-found">Post not found</div>
                )}
            </div>

            <Footer />
        </div>
    );
}

export default BlogPostPage; 